import { zodResolver } from '@hookform/resolvers/zod'
import { CalendarClock, Loader2, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { DateField } from '@/components/ui/date-field'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { NumberInput } from '@/components/ui/number-input'
import { FormSelect, Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { fmtDate, fmtMoney } from '../../shared/format'
import { useDeleteObligationRate, useObligation, useSaveObligation, useSaveObligationRate } from '../api'
import {
  OBLIGATION_KINDS,
  RECURRENCES,
  REMIND_DAY_OPTIONS,
  VAT_RATES,
  WITHHOLDING_RATES,
  type Obligation,
} from '../types'
import { toastError, useFinanceLookups } from '../ui'
import { CheckRow, Field, FormSection } from './FormBits'
import { useFinanceDepartments } from '../departments'

type Props = { open: boolean; obligation: Obligation | null; onOpenChange: (v: boolean) => void }

/** Ödeme planı tanımı: tekrar, vade günü, kategori ve hatırlatma; tutar değişiklikleri oran geçmişine eklenir. */
export function ObligationFormModal({ open, obligation, onOpenChange }: Props) {
  return open ? (
    <ObligationForm key={obligation?.id ?? 'new'} obligation={obligation} onDone={() => onOpenChange(false)} />
  ) : null
}

const today = () => new Date().toISOString().slice(0, 10)
const amountRe = /^\d+(\.\d{1,2})?$/

function ObligationForm({ obligation, onDone }: { obligation: Obligation | null; onDone: () => void }) {
  const { t } = useTranslation()
  const save = useSaveObligation()
  const { categories, parties, accounts, accountDetail } = useFinanceLookups()
  const editing = !!obligation
  const system = obligation?.source === 'payroll'

  const departments = useFinanceDepartments()
  const schema = z.object({
    title: z.string().trim().min(1, t('finance.obligations.form.titleRequired')).max(160),
    kind: z.enum(OBLIGATION_KINDS),
    direction: z.enum(['out', 'in']),
    categoryId: z.string(),
    partyId: z.string(),
    accountId: z.string(),
    recurrence: z.enum(RECURRENCES),
    dueDay: z.number().int().min(1).max(31),
    startDate: z.string().min(1, t('finance.form.dateRequired')),
    endDate: z.string(),
    periodOffset: z.number().int().min(0).max(3),
    vatRate: z.number(),
    withholdingRate: z.number(),
    remindDays: z.array(z.number()),
    notes: z.string().max(1000),
    departmentId: z.string(),
    active: z.boolean(),
    amount: editing
      ? z.string()
      : z
          .string()
          .regex(amountRe, t('finance.form.amountInvalid'))
          .refine((v) => Number(v) > 0, t('finance.form.amountInvalid')),
  })
  const o = obligation
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      title: o?.title ?? '',
      kind: o?.kind ?? 'rent',
      direction: o?.direction ?? 'out',
      categoryId: o?.categoryId ?? '',
      partyId: o?.partyId ?? '',
      accountId: o?.accountId ?? '',
      recurrence: o?.recurrence ?? 'monthly',
      dueDay: o?.dueDay ?? new Date().getDate(),
      startDate: o?.startDate ?? today(),
      endDate: o?.endDate ?? '',
      periodOffset: o?.periodOffset ?? 0,
      vatRate: o?.vatRate ?? 0,
      withholdingRate: o?.withholdingRate ?? 0,
      remindDays: o?.remindDays ?? [7, 3, 1, 0],
      notes: o?.notes ?? '',
      departmentId: o?.departmentId ?? '',
      active: o?.active ?? true,
      amount: '',
    },
  })
  const { errors } = form.formState
  const [direction, recurrence] = useWatch({ control: form.control, name: ['direction', 'recurrence'] })
  const catKind = direction === 'in' ? 'income' : 'expense'

  const onSubmit = form.handleSubmit(async ({ amount, ...v }) => {
    try {
      await save.mutateAsync({ id: o?.id, input: { ...v, amount: editing ? undefined : amount } })
      toast.success(t(editing ? 'finance.obligations.updated' : 'finance.obligations.created'))
      onDone()
    } catch (e) {
      toastError(e)
    }
  })

  return (
    <Modal
      open
      size="lg"
      onOpenChange={(v) => !v && onDone()}
      dismissible={!save.isPending}
      icon={<CalendarClock />}
      title={t(editing ? 'finance.obligations.form.editTitle' : 'finance.obligations.form.newTitle')}
      description={t(system ? 'finance.obligations.form.systemHint' : 'finance.obligations.form.hint')}
      onSubmit={(e) => void onSubmit(e)}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onDone}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? <Loader2 className="animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <FormSection title={t('finance.obligations.form.sectionWhat')}>
          <Field id="ob-title" label={t('finance.obligations.form.title')} error={errors.title?.message} full>
            <Input
              id="ob-title"
              maxLength={160}
              placeholder={t('finance.obligations.form.titlePlaceholder')}
              {...form.register('title')}
            />
          </Field>
          <Field id="ob-direction" label={t('finance.obligations.form.direction')}>
            <FormSelect
              id="ob-direction"
              control={form.control}
              name="direction"
              disabled={system}
              onValueChange={() => form.setValue('categoryId', '')}
              options={(['out', 'in'] as const).map((d) => ({
                value: d,
                label: t(`finance.obligations.direction.${d}`),
              }))}
            />
          </Field>
          <Field id="ob-kind" label={t('finance.obligations.form.kind')}>
            <FormSelect
              id="ob-kind"
              control={form.control}
              name="kind"
              disabled={system}
              options={OBLIGATION_KINDS.map((k) => ({ value: k, label: t(`finance.obligations.kinds.${k}`) }))}
            />
          </Field>
          <Field
            id="ob-cat"
            label={t('finance.obligations.form.category')}
            hint={t('finance.obligations.form.categoryHint')}
          >
            <FormSelect
              id="ob-cat"
              control={form.control}
              name="categoryId"
              placeholder={t('finance.obligations.form.auto')}
              options={categories
                .filter((c) => c.kind === catKind && c.active)
                .map((c) => ({ value: c.id, label: c.name }))}
            />
          </Field>
          {departments.data?.length ? (
            <Field id="ob-dept" label={t('finance.departments.field')} hint={t('finance.departments.fieldHint')}>
              <FormSelect
                id="ob-dept"
                control={form.control}
                name="departmentId"
                options={[{ value: '', label: t('finance.departments.categoryDefault') }, ...departments.data.map((d) => ({ value: d.id, label: d.name }))]}
              />
            </Field>
          ) : null}
          <Field id="ob-party" label={t('finance.obligations.form.party')}>
            <FormSelect
              id="ob-party"
              control={form.control}
              name="partyId"
              placeholder={t('finance.obligations.form.optional')}
              options={[
                { value: '', label: '—' },
                ...parties.map((p) => ({ value: p.id, label: p.name, keywords: [p.taxNo] })),
              ]}
            />
          </Field>
          <Field
            id="ob-account"
            label={t('finance.obligations.form.account')}
            hint={t('finance.obligations.form.accountHint')}
            full
          >
            <FormSelect
              id="ob-account"
              control={form.control}
              name="accountId"
              placeholder={t('finance.obligations.form.optional')}
              options={[
                { value: '', label: '—' },
                ...accounts
                  .filter((a) => a.active)
                  .map((a) => ({ value: a.id, label: a.name, description: accountDetail(a.id) })),
              ]}
            />
          </Field>
        </FormSection>

        <FormSection title={t('finance.obligations.form.sectionWhen')}>
          <Field id="ob-rec" label={t('finance.obligations.form.recurrence')}>
            <FormSelect
              id="ob-rec"
              control={form.control}
              name="recurrence"
              disabled={system}
              options={RECURRENCES.map((r) => ({ value: r, label: t(`finance.obligations.recurrence.${r}`) }))}
            />
          </Field>
          <Field
            id="ob-day"
            label={t('finance.obligations.form.dueDay')}
            error={errors.dueDay?.message}
            hint={t('finance.obligations.form.dueDayHint')}
          >
            <Controller
              control={form.control}
              name="dueDay"
              render={({ field }) => (
                <NumberInput
                  id="ob-day"
                  value={String(field.value)}
                  maxDecimals={0}
                  onChange={(v) => field.onChange(Math.min(31, Math.max(1, Number(v) || 1)))}
                  disabled={recurrence === 'none' || system}
                />
              )}
            />
          </Field>
          <Field
            id="ob-start"
            label={t(recurrence === 'none' ? 'finance.obligations.form.dueDate' : 'finance.obligations.form.startDate')}
            error={errors.startDate?.message}
          >
            <Controller
              control={form.control}
              name="startDate"
              render={({ field }) => (
                <DateField
                  id="ob-start"
                  value={field.value}
                  onChange={field.onChange}
                  clearable={false}
                  disabled={system}
                />
              )}
            />
          </Field>
          {recurrence !== 'none' ? (
            <Field
              id="ob-end"
              label={t('finance.obligations.form.endDate')}
              hint={t('finance.obligations.form.endDateHint')}
            >
              <Controller
                control={form.control}
                name="endDate"
                render={({ field }) => (
                  <DateField id="ob-end" value={field.value} onChange={field.onChange} disabled={system} />
                )}
              />
            </Field>
          ) : null}
          <Field
            id="ob-offset"
            label={t('finance.obligations.form.periodOffset')}
            hint={t('finance.obligations.form.periodOffsetHint')}
          >
            <Controller
              control={form.control}
              name="periodOffset"
              render={({ field }) => (
                <FormSelectNumber
                  id="ob-offset"
                  value={field.value}
                  onChange={field.onChange}
                  disabled={system}
                  options={[0, 1, 2, 3].map((n) => ({ value: n, label: t(`finance.obligations.offset.${n}`) }))}
                />
              )}
            />
          </Field>
          <Controller
            control={form.control}
            name="remindDays"
            render={({ field }) => (
              <fieldset className="space-y-2 sm:col-span-2">
                <legend className="mb-1 text-sm font-medium">{t('finance.obligations.form.remind')}</legend>
                <div className="flex flex-wrap gap-2">
                  {REMIND_DAY_OPTIONS.map((d) => {
                    const on = field.value.includes(d)
                    return (
                      <button
                        key={d}
                        type="button"
                        aria-pressed={on}
                        onClick={() =>
                          field.onChange(
                            on ? field.value.filter((x) => x !== d) : [...field.value, d].sort((a, b) => b - a),
                          )
                        }
                        className={cn(
                          'rounded-full border px-3 py-1 text-xs transition-colors',
                          on ? 'border-primary/50 bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted',
                        )}
                      >
                        {d === 0
                          ? t('finance.obligations.remindDue')
                          : t('finance.obligations.remindBefore', { count: d })}
                      </button>
                    )
                  })}
                </div>
                <p className="text-muted-foreground text-xs">{t('finance.obligations.form.remindHint')}</p>
              </fieldset>
            )}
          />
        </FormSection>

        <FormSection title={t('finance.obligations.form.sectionAmount')}>
          {!editing ? (
            <Field
              id="ob-amount"
              label={t('finance.obligations.form.amount')}
              error={errors.amount?.message}
              hint={t('finance.obligations.form.amountHint')}
            >
              <Controller
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <NumberInput
                    id="ob-amount"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    minDecimalsOnBlur={2}
                    suffix="₺"
                    aria-invalid={!!errors.amount}
                  />
                )}
              />
            </Field>
          ) : null}
          <Field id="ob-vat" label={t('finance.tx.vatRate')}>
            <Controller
              control={form.control}
              name="vatRate"
              render={({ field }) => (
                <FormSelectNumber
                  id="ob-vat"
                  value={field.value}
                  onChange={field.onChange}
                  options={VAT_RATES.map((r) => ({ value: r, label: `%${r}` }))}
                />
              )}
            />
          </Field>
          <Field id="ob-wh" label={t('finance.tx.withholdingRate')}>
            <Controller
              control={form.control}
              name="withholdingRate"
              render={({ field }) => (
                <FormSelectNumber
                  id="ob-wh"
                  value={field.value}
                  onChange={field.onChange}
                  options={WITHHOLDING_RATES.map((r) => ({ value: r, label: `%${r}` }))}
                />
              )}
            />
          </Field>
          <Field id="ob-notes" label={t('finance.obligations.form.note')} full>
            <Textarea id="ob-notes" rows={2} maxLength={1000} {...form.register('notes')} />
          </Field>
          {editing ? (
            <CheckRow {...form.register('active')} hint={t('finance.obligations.form.activeHint')}>
              {t('finance.obligations.form.active')}
            </CheckRow>
          ) : null}
        </FormSection>

        {o ? <RateHistory id={o.id} locked={system} /> : null}
      </div>
    </Modal>
  )
}

function FormSelectNumber({
  id,
  value,
  onChange,
  options,
  disabled,
}: {
  id: string
  value: number
  onChange: (v: number) => void
  options: { value: number; label: string }[]
  disabled?: boolean
}) {
  return (
    <Select
      id={id}
      disabled={disabled}
      value={String(value)}
      onChange={(v) => onChange(Number(v))}
      options={options.map((o) => ({ value: String(o.value), label: o.label }))}
    />
  )
}

/** Dönemsel tutarlar: yeni tutar geçerlilik tarihinden itibaren bekleyen vadelere uygulanır; ödenmişler değişmez. */
function RateHistory({ id, locked }: { id: string; locked: boolean }) {
  const { t } = useTranslation()
  const detail = useObligation(id)
  const saveRate = useSaveObligationRate()
  const delRate = useDeleteObligationRate()
  const [validFrom, setValidFrom] = useState(today())
  const [amount, setAmount] = useState('')
  const [note, setNote] = useState('')
  const valid = amountRe.test(amount) && Number(amount) >= 0 && !!validFrom
  const add = () =>
    saveRate.mutate(
      { id, validFrom, amount, note },
      {
        onSuccess: () => {
          setAmount('')
          setNote('')
          toast.success(t('finance.obligations.rateSaved'))
        },
        onError: toastError,
      },
    )
  const rates = detail.data?.rates ?? []
  return (
    <FormSection title={t('finance.obligations.form.sectionRates')}>
      <div className="sm:col-span-2">
        {detail.isLoading ? (
          <Skeleton className="h-16" />
        ) : (
          <ul className="divide-y rounded-lg border">
            {rates.map((r) => (
              <li key={r.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                <span className="text-muted-foreground w-28 tabular-nums">{fmtDate(r.validFrom)}</span>
                <span className="flex-1 font-medium tabular-nums">{fmtMoney(r.amount)}</span>
                <span className="text-muted-foreground hidden min-w-0 flex-1 truncate text-xs sm:block">{r.note}</span>
                {!locked && rates.length > 1 ? (
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    aria-label={t('common.delete')}
                    disabled={delRate.isPending}
                    onClick={() => delRate.mutate({ id, rateId: r.id }, { onError: toastError })}
                  >
                    <Trash2 />
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
      {!locked ? (
        <div className="grid grid-cols-1 gap-2 sm:col-span-2 sm:grid-cols-[10rem_9rem_1fr_auto] sm:items-end">
          <Field id="ob-rate-from" label={t('finance.obligations.form.validFrom')}>
            <DateField id="ob-rate-from" value={validFrom} onChange={setValidFrom} clearable={false} />
          </Field>
          <Field id="ob-rate-amount" label={t('finance.obligations.form.newAmount')}>
            <NumberInput id="ob-rate-amount" value={amount} onChange={setAmount} minDecimalsOnBlur={2} suffix="₺" />
          </Field>
          <Field id="ob-rate-note" label={t('finance.obligations.form.note')}>
            <Input
              id="ob-rate-note"
              value={note}
              maxLength={120}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('finance.obligations.form.rateNotePlaceholder')}
            />
          </Field>
          <Button type="button" variant="outline" disabled={!valid || saveRate.isPending} onClick={add}>
            {saveRate.isPending ? <Loader2 className="animate-spin" /> : <Plus />}
            {t('finance.obligations.form.addRate')}
          </Button>
        </div>
      ) : null}
    </FormSection>
  )
}
